// Slice 41 — Issue License modal. Three stages:
//   1. form     → customer-id, tier, expiry-days, max-instances, HWID,
//                 feature flags, internal note
//   2. signing  → spinner while POST /admin/licenses round-trips to the
//                 air-gapped Slice 38 signing-proxy
//   3. success  → license-id mono block + copy button + signed-JSON pre
//                 + "Download .vyvern-license" (Blob/URL download)

import { useEffect, useState } from "react";
import {
  Api,
  FEATURE_FLAGS,
  type FeatureFlag,
  type IssueLicenseInput,
  type SignedLicense,
  type Tier,
} from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import {
  HqBadge,
  HqButton,
  HqCheckbox,
  HqIconButton,
  HqInput,
  HqModal,
  HqRadio,
  HqSelect,
  Icons,
} from "../../ui";

const TIER_CARDS: { value: Tier; label: string; blurb: string }[] = [
  { value: "beta", label: "Beta", blurb: "Pro features · soft-expiry watermark." },
  { value: "pro", label: "Pro", blurb: "Full feature set · perpetual or dated." },
  { value: "free", label: "Free", blurb: "Single instance · no premium flags." },
];

const MAX_INSTANCES_OPTIONS = [
  { value: "-1", label: "Unlimited" },
  { value: "1", label: "1" },
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "25", label: "25" },
];

interface FormState {
  customerId: string;
  tier: Tier;
  expiresDays: string;
  maxInstances: string;
  bindHwid: boolean;
  hwidFingerprint: string;
  features: Set<FeatureFlag>;
  note: string;
}

type Stage =
  | { kind: "form"; err: string | null }
  | { kind: "signing" }
  | { kind: "success"; license: SignedLicense };

const INITIAL: FormState = {
  customerId: "",
  tier: "beta",
  expiresDays: "90",
  maxInstances: "-1",
  bindHwid: false,
  hwidFingerprint: "",
  features: new Set(),
  note: "",
};

export interface IssueLicenseModalProps {
  open: boolean;
  onClose: () => void;
  onIssued: (license: SignedLicense) => void;
}

export function IssueLicenseModal({ open, onClose, onIssued }: IssueLicenseModalProps) {
  const { toast } = useShell();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [stage, setStage] = useState<Stage>({ kind: "form", err: null });

  // Reset whenever the modal closes so re-opens start fresh.
  useEffect(() => {
    if (!open) {
      setForm(INITIAL);
      setStage({ kind: "form", err: null });
    }
  }, [open]);

  async function submit() {
    const cust = form.customerId.trim();
    if (!cust) {
      setStage({ kind: "form", err: "Customer ID required." });
      return;
    }
    const max = Number(form.maxInstances);
    if (!Number.isFinite(max)) {
      setStage({ kind: "form", err: "Max instances must be a number." });
      return;
    }
    const days = form.expiresDays.trim();
    let expiresAt: number | undefined;
    if (days !== "") {
      const n = Number(days);
      if (!Number.isFinite(n) || n <= 0) {
        setStage({
          kind: "form",
          err: "Expiry days must be a positive number (blank = perpetual).",
        });
        return;
      }
      expiresAt = Math.floor(Date.now() / 1000) + Math.floor(n * 86400);
    }
    if (form.bindHwid && !form.hwidFingerprint.trim()) {
      setStage({ kind: "form", err: "HWID fingerprint required when HWID-binding is on." });
      return;
    }

    setStage({ kind: "signing" });
    const input: IssueLicenseInput = {
      customerId: cust,
      tier: form.tier,
      maxInstances: max,
      betaProgram: form.tier === "beta",
      features: Array.from(form.features),
      ...(expiresAt ? { expiresAt } : {}),
      ...(form.bindHwid && form.hwidFingerprint.trim()
        ? { hwidFingerprint: form.hwidFingerprint.trim() }
        : {}),
    };
    const res = await Api.issueLicense(input);
    if (!res.ok) {
      setStage({ kind: "form", err: res.error.message });
      toast(`Sign failed: ${res.error.message}`, "danger");
      return;
    }
    setStage({ kind: "success", license: res.data.license });
  }

  function close() {
    onClose();
    if (stage.kind === "success") onIssued(stage.license);
  }

  return (
    <HqModal
      open={open}
      onClose={close}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Key width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
          Issue license
        </span>
      }
      width={620}
      footer={
        stage.kind === "success" ? (
          <SuccessFooter license={stage.license} onClose={close} />
        ) : (
          <FormFooter onCancel={close} onSubmit={submit} busy={stage.kind === "signing"} />
        )
      }
    >
      {stage.kind === "success" ? (
        <SuccessBody license={stage.license} />
      ) : stage.kind === "signing" ? (
        <SigningBody />
      ) : (
        <FormBody form={form} setForm={setForm} err={stage.err} />
      )}
    </HqModal>
  );
}

function FormBody({
  form,
  setForm,
  err,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  err: string | null;
}) {
  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm({ ...form, [k]: v });
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Customer ID">
        <HqInput
          value={form.customerId}
          onChange={(e) => update("customerId", e.target.value)}
          placeholder="acme-1   (free-text until Slice 43 ships customers)"
          mono
          autoFocus
        />
      </Field>
      <Field label="Tier">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
          }}
        >
          {TIER_CARDS.map((t) => (
            <TierCard
              key={t.value}
              card={t}
              active={form.tier === t.value}
              onClick={() => update("tier", t.value)}
            />
          ))}
        </div>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Expires" hint="Blank = perpetual.">
          <div style={{ position: "relative" }}>
            <HqInput
              value={form.expiresDays}
              onChange={(e) => update("expiresDays", e.target.value)}
              placeholder="90"
              type="number"
              mono
            />
            <span
              className="hq-mono"
              style={{
                position: "absolute",
                right: 12,
                top: 9,
                fontSize: 10.5,
                color: "var(--hq-text-dim)",
                pointerEvents: "none",
              }}
            >
              days
            </span>
          </div>
        </Field>
        <Field label="Max instances">
          <HqSelect
            options={MAX_INSTANCES_OPTIONS}
            value={form.maxInstances}
            onChange={(v) => update("maxInstances", v)}
          />
        </Field>
      </div>
      <Field
        label="HWID binding"
        hint="When on, the license refuses to start on any other machine."
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, color: "var(--hq-text-muted)" }}>
            {form.bindHwid ? "Bound to a specific fingerprint" : "Unbound (any host)"}
          </span>
          <span style={{ flex: 1 }} />
          <HqIconButton
            icon={form.bindHwid ? Icons.Lock : Icons.Plus}
            size="sm"
            title={form.bindHwid ? "Disable HWID binding" : "Enable HWID binding"}
            onClick={() => update("bindHwid", !form.bindHwid)}
          />
        </div>
        {form.bindHwid && (
          <div style={{ marginTop: 8 }}>
            <HqInput
              value={form.hwidFingerprint}
              onChange={(e) => update("hwidFingerprint", e.target.value)}
              placeholder="sha256:…   (run `vyvern hwid --json` on the target)"
              mono
            />
          </div>
        )}
      </Field>
      <Field label="Feature flags" hint="Only meaningful for paid tiers.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {FEATURE_FLAGS.map((flag) => (
            <FlagChip
              key={flag}
              label={flag}
              active={form.features.has(flag)}
              onToggle={(next) => {
                const set = new Set(form.features);
                if (next) set.add(flag);
                else set.delete(flag);
                update("features", set);
              }}
            />
          ))}
        </div>
      </Field>
      <Field label="Internal note" hint="Not embedded in the license; HQ-only.">
        <textarea
          value={form.note}
          onChange={(e) => update("note", e.target.value)}
          rows={3}
          style={{
            width: "100%",
            background: "var(--hq-bg-input)",
            color: "var(--hq-text)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            padding: "10px 12px",
            fontSize: 12.5,
            fontFamily: "var(--hq-font-sans)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Field>
      {err && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
}

function TierCard({
  card,
  active,
  onClick,
}: {
  card: { value: Tier; label: string; blurb: string };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 12,
        borderRadius: "var(--hq-radius-lg)",
        background: active ? "var(--hq-bg-active)" : "var(--hq-bg-input)",
        border: `1px solid ${active ? "rgba(34,211,238,.45)" : "var(--hq-border-input)"}`,
        color: "var(--hq-text)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        boxShadow: active ? "0 0 0 1px rgba(34,211,238,.25)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HqRadio checked={active} onChange={() => onClick()} ariaLabel={card.label} />
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{card.label}</span>
      </div>
      <span style={{ fontSize: 11, color: "var(--hq-text-dim)", lineHeight: 1.4 }}>
        {card.blurb}
      </span>
    </button>
  );
}

function FlagChip({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!active)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: "var(--hq-radius-md)",
        background: active ? "var(--hq-accent-soft)" : "var(--hq-bg-input)",
        color: active ? "var(--hq-accent)" : "var(--hq-text-muted)",
        border: `1px solid ${active ? "rgba(34,211,238,.35)" : "var(--hq-border-input)"}`,
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      <HqCheckbox checked={active} onChange={onToggle} ariaLabel={label} />
      {label}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", fontWeight: 500 }}>
          {label}
        </span>
        {hint && <span style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function SigningBody() {
  return (
    <div
      style={{
        padding: "32px 12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <Icons.Key
        width={32}
        height={32}
        sw={1.6}
        style={{ color: "var(--hq-accent)" }}
        className="hq-spin"
      />
      <div
        className="hq-mono"
        style={{
          fontSize: 11,
          letterSpacing: ".18em",
          textTransform: "uppercase",
          color: "var(--hq-text-dim)",
        }}
      >
        Signing license bytes (Ed25519)…
      </div>
      <div
        style={{ fontSize: 11, color: "var(--hq-text-faint)", textAlign: "center", maxWidth: 380 }}
      >
        The HQ web layer holds no key; this request is round-tripping through the air-gapped
        signing-proxy (Slice 38) on the keygen host.
      </div>
    </div>
  );
}

function SuccessBody({ license }: { license: SignedLicense }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          borderRadius: "var(--hq-radius-lg)",
          background: "var(--hq-success-soft)",
          border: "1px solid rgba(52,211,153,.25)",
          color: "var(--hq-success)",
          fontSize: 12.5,
        }}
      >
        <Icons.Check width={14} height={14} sw={2} />
        Signed and persisted. Hand the file below to the customer.
      </div>
      <Field label="License ID">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            fontFamily: "var(--hq-font-mono)",
            fontSize: 12,
          }}
        >
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
            {license.licenseId}
          </span>
          <HqIconButton
            icon={Icons.Copy}
            size="sm"
            title="Copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(license.licenseId);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {
                /* ignore */
              }
            }}
          />
          {copied && <HqBadge tone="success">copied</HqBadge>}
        </div>
      </Field>
      <Field label="Signed JSON" hint="Verifiable against the embedded dev/prod pubkey.">
        <pre
          className="hq-mono hq-scroll"
          style={{
            margin: 0,
            padding: "10px 12px",
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            color: "var(--hq-text-muted)",
            fontSize: 10.5,
            maxHeight: 180,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {JSON.stringify(license, null, 2)}
        </pre>
      </Field>
    </div>
  );
}

function FormFooter({
  onCancel,
  onSubmit,
  busy,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  return (
    <>
      <HqButton variant="ghost" onClick={onCancel}>
        Cancel
      </HqButton>
      <HqButton variant="primary" leadingIcon={Icons.Sparkles} onClick={onSubmit} loading={busy}>
        Sign &amp; generate
      </HqButton>
    </>
  );
}

function SuccessFooter({
  license,
  onClose,
}: {
  license: SignedLicense;
  onClose: () => void;
}) {
  function download() {
    const blob = new Blob([`${JSON.stringify(license, null, 2)}\n`], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${license.licenseId}.vyvern-license`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  return (
    <>
      <HqButton variant="ghost" onClick={onClose}>
        Close
      </HqButton>
      <HqButton variant="primary" leadingIcon={Icons.Download} onClick={download}>
        Download .vyvern-license
      </HqButton>
    </>
  );
}
